'use client';
import { useMemoizedFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useRef } from 'react';
import CreateGuide from '@/app/assets/imgs/create_guide.svg';
import { FeedCard, FeedList, PostReferenceCard, useFeedCard } from '@/app/components/shared/Community';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import HorizontalCarousel, { SCREEN_WIDTH_MAP } from '@/app/components/shared/HorizontalCarousel';
import { ScreenType, TAB_TYPE_MAP } from '@/app/constants';
import type { ScreenConfigType } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { NewShowCaseList, PostReferenceItem } from '@/app/types';
import { CommunityEnum, type InspirationItem, type UpdateInteractionStatusFn } from '@/app/types';
import { mapTypeToExploreTab, reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import { usePublishedData } from '../_hooks/use-publish-data';
import { usePublishedDetail } from '../_hooks/use-publish-detail';
import { PAGE_SIZE, shouldLimitGuestProfileModules } from '../constant';
import type { InspirationFetchDataResult, PostItem, ReferenceFetchDataResult, ReferenceSubmissionItem } from '../types';

const CarouselClassMap: ScreenConfigType<SCREEN_WIDTH_MAP> = {
  // 6个半
  [ScreenType.QHD_PLUS]: SCREEN_WIDTH_MAP.SIX_AND_A_HALF,
  // 5个半
  [ScreenType.QHD]: SCREEN_WIDTH_MAP.FIVE_AND_A_HALF,
  [ScreenType.DEFAULT]: SCREEN_WIDTH_MAP.THREE_AND_A_HALF,
  // 2个半
  [ScreenType.SM]: SCREEN_WIDTH_MAP.TWO_AND_A_HALF,
  placeholder: SCREEN_WIDTH_MAP.THREE_AND_A_HALF,
};

export const isInspirationItem = (item: PostItem): item is InspirationItem => {
  return 'media_asset' in item || 'short_film' in item || 'tutorial' in item;
};

const SectionBlock = ({ title, children }: { title: string; children: ReactNode }) => {
  return (
    <div>
      <div className="mb-4 font-medium text-xl sm:text-sm">{title}</div>
      {children}
    </div>
  );
};

const EmptyContent = () => {
  const t = useTranslations();
  return (
    <div className="flex h-[calc(100vh/2)] items-center justify-center">
      <div className="flex h-full w-[400px] flex-col items-center justify-center lg:w-[320px]">
        <img
          src={CreateGuide.src}
          alt="create_guide"
          className="mb-8 w-[400px] opacity-60 sm:w-[200px] lg:mb-6 lg:w-[320px]"
        />
        <div className="mb-10 text-center">{t('profile.empty.published')}</div>
      </div>
    </div>
  );
};

interface CarouselSectionProps<RecordItem> {
  title: string;
  list: RecordItem[];
  type: CommunityEnum;
  onItemExpose?: (idx: number, type: CommunityEnum) => void;
  onLoadMore: (idx: number, type: CommunityEnum) => void;
  renderItem: (item: RecordItem, type: CommunityEnum) => ReactNode;
}

const CarouselSection = <ListItem extends { id?: string }>({
  title,
  list,
  type,
  onItemExpose,
  onLoadMore,
  renderItem,
}: CarouselSectionProps<ListItem>) => {
  if (list.length === 0) return null;

  return (
    <SectionBlock title={title}>
      <HorizontalCarousel<ListItem>
        onItemExpose={(idx) => onItemExpose?.(idx, type)}
        list={list}
        renderItem={(item) => renderItem(item, type)}
        onNext={(idx) => onLoadMore(idx, type)}
        widthClassMap={CarouselClassMap}
      />
    </SectionBlock>
  );
};

interface InspirationSectionProps {
  title: string;
  list: InspirationItem[];
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  userId?: string;
  handleDetail: (id: string, traceId?: string) => void;
  handleVote: UpdateInteractionStatusFn;
  handleUnVote: UpdateInteractionStatusFn;
  onLoadMore: () => Promise<void>;
}

const InspirationSection = ({
  title,
  list,
  total,
  hasMore,
  isLoading,
  userId,
  handleDetail,
  handleVote,
  handleUnVote,
  onLoadMore,
}: InspirationSectionProps) => {
  if (list.length === 0) return null;
  return (
    <SectionBlock title={title}>
      <FeedList
        scrollElementId="scrollableDiv"
        inspAssets={list}
        handleDetail={handleDetail}
        handleUnVote={handleUnVote}
        handleVote={handleVote}
        referenceList={[]}
        hideEmptyVoteCount={!!userId}
        showSelected={true}
        active={CommunityEnum.MediaAsset}
        hideUser={true}
        hasMore={hasMore && total > list.length}
        isLoading={isLoading}
        onLoadMore={onLoadMore}
      />
    </SectionBlock>
  );
};

interface PublishedProps {
  userId?: string;
  objId?: string;
  objType?: string;
}
export const Published = ({ userId, objId, objType }: PublishedProps) => {
  const t = useTranslations();
  const pathname = usePathname();
  const referenceSectionRef = useRef<HTMLDivElement>(null);
  const shortFilmSectionRef = useRef<HTMLDivElement>(null);
  const tutorialSectionRef = useRef<HTMLDivElement>(null);
  const inspirationSectionRef = useRef<HTMLDivElement>(null);
  const shouldLimitPreview = shouldLimitGuestProfileModules(userId);

  const syncFeedDetailConfig = useStore((state) => state.syncFeedDetailConfig);

  const { shortFilmData, tutorialData, inspirationData, referenceData, pageLoading, hasData } =
    usePublishedData(userId);

  const { detailConfig } = usePublishedDetail({
    shortFilmData,
    tutorialData,
    inspirationData,
  });

  useEffect(() => {
    if (objId || objType) {
      if (
        (objType === CommunityEnum.MediaAsset ||
          objType === CommunityEnum.ImageAsset ||
          objType === CommunityEnum.MiniVideo) &&
        inspirationData.state.list.length > 0
      ) {
        if (objId) {
          inspirationData.handleDetail(objId);
        }
        requestAnimationFrame(() => {
          inspirationSectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        });
      }
      if (objType === CommunityEnum.ShortFilm && shortFilmData.state.list.length > 0) {
        if (objId) {
          shortFilmData.handleDetail(objId);
        }
        requestAnimationFrame(() => {
          shortFilmSectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        });
      }
      if (objType === CommunityEnum.Tutorial && tutorialData.state.list.length > 0) {
        if (objId) {
          tutorialData.handleDetail(objId);
        }
        requestAnimationFrame(() => {
          tutorialSectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        });
      }
      if (objType === CommunityEnum.PostReferences && referenceData.state.list.length > 0) {
        if (objId) {
          referenceData.handleDetail(objId);
        }
        requestAnimationFrame(() => {
          referenceSectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        });
      }
    }
  }, [
    objId,
    objType,
    inspirationData.state.list,
    shortFilmData.state.list,
    tutorialData.state.list,
    referenceData.state.list,
  ]);

  useEffect(() => {
    syncFeedDetailConfig({
      ...detailConfig?.detail,
    });
  }, [detailConfig?.detail.thumbScrollListConfig.list, detailConfig?.detail.thumbScrollListConfig.hasMore]);

  const shortFilmHook = useFeedCard({
    handleDetail: shortFilmData.handleDetail,
    handleVote: shortFilmData.vote,
    handleUnVote: shortFilmData.unVote,
  });

  const tutorialHook = useFeedCard({
    handleDetail: tutorialData.handleDetail,
    handleVote: tutorialData.vote,
    handleUnVote: tutorialData.unVote,
  });

  const renderItem = useMemoizedFn((item: PostItem, type: CommunityEnum) => {
    if (isInspirationItem(item)) {
      const getCardInfo = type === CommunityEnum.ShortFilm ? shortFilmHook.getCardInfo : tutorialHook.getCardInfo;
      return (
        <FeedCard
          item={item}
          {...getCardInfo({
            item,
            hideUser: true,
          })}
          tag={(item as NewShowCaseList[number]).selected ? t('creations.videoCard.officialSelected') : ''}
          tooltipContent={t('creations.videoCard.officialSelectedTooltip')}
          forceVideoAspectRatio={true}
          hideEmptyVoteCount={!!userId}
        />
      );
    }
    return (
      <PostReferenceCard
        data={item as PostReferenceItem}
        handleDetail={() =>
          referenceData.handleDetail(item.element?.id || '', userId ? 'guest_profile' : 'my_published_page')
        }
        forceVideoAspectRatio={true}
      />
    );
  });

  // 处理项目曝光
  const handleItemExpose = useMemoizedFn((idx: number, type: CommunityEnum) => {
    const dataMap: Partial<Record<CommunityEnum, ReferenceFetchDataResult | InspirationFetchDataResult>> = {
      [CommunityEnum.ShortFilm]: shortFilmData,
      [CommunityEnum.Tutorial]: tutorialData,
      [CommunityEnum.PostReferences]: referenceData,
    };

    const data = dataMap[type];
    if (!data) return;
    if (type === CommunityEnum.PostReferences) {
      referenceData?.onExpose?.(idx);
    } else {
      const item = data.state.list[idx] as InspirationItem;
      const submitId = type === CommunityEnum.ShortFilm ? item?.short_film?.id : item?.tutorial?.id;

      reportEvent('submission_show', {
        submit_id: submitId || '',
        submit_type: TAB_TYPE_MAP[mapTypeToExploreTab(type)],
        show_position: pathname,
        index: idx,
      });
    }

    handleLoadMore(idx, type);
  });

  // 处理加载更多
  const handleLoadMore = useMemoizedFn((idx: number, type: CommunityEnum) => {
    const dataMap: Partial<Record<CommunityEnum, ReferenceFetchDataResult | InspirationFetchDataResult>> = {
      [CommunityEnum.ShortFilm]: shortFilmData,
      [CommunityEnum.Tutorial]: tutorialData,
      [CommunityEnum.PostReferences]: referenceData,
    };

    const data = dataMap[type];
    if (!data) return;
    const shouldLoadMore =
      data.state.hasMore &&
      idx > data.state.list.length - PAGE_SIZE &&
      data.state.page * PAGE_SIZE < data.state.total &&
      !data.fetchReq.loading;

    if (shouldLoadMore) {
      data.setState({ page: data.state.page + 1 });
    }
  });

  // 处理灵感区块加载更多
  const handleInspirationLoadMore = useMemoizedFn(async () => {
    if (shouldLimitPreview || !inspirationData.state.hasMore) return;

    inspirationData.setState({
      page: inspirationData.state.page + 1,
    });
  });

  if (pageLoading) {
    return (
      <div className="my-10">
        <GlobalLoader />
      </div>
    );
  }

  if (!hasData) {
    return <EmptyContent />;
  }

  return (
    <div className="space-y-6">
      <div ref={shortFilmSectionRef}>
        <CarouselSection<InspirationItem>
          title={t('homeTab.shortFilm')}
          list={shortFilmData.state.list}
          type={CommunityEnum.ShortFilm}
          onItemExpose={handleItemExpose}
          onLoadMore={handleLoadMore}
          renderItem={renderItem}
        />
      </div>
      <div ref={tutorialSectionRef}>
        <CarouselSection<InspirationItem>
          title={t('homeTab.tutorial')}
          list={tutorialData.state.list}
          type={CommunityEnum.Tutorial}
          onItemExpose={handleItemExpose}
          onLoadMore={handleLoadMore}
          renderItem={renderItem}
        />
      </div>
      <div ref={referenceSectionRef}>
        <CarouselSection<ReferenceSubmissionItem>
          title={t('homeTab.sharedReferences')}
          list={referenceData.state.list}
          type={CommunityEnum.PostReferences}
          onItemExpose={handleItemExpose}
          onLoadMore={handleLoadMore}
          renderItem={renderItem}
        />
      </div>
      <div ref={inspirationSectionRef}>
        <InspirationSection
          title={t('homeTab.mediaAsset')}
          list={inspirationData.state.list}
          total={inspirationData.state.total}
          hasMore={inspirationData.state.hasMore}
          isLoading={inspirationData.fetchReq.loading}
          userId={userId}
          handleDetail={inspirationData.handleDetail}
          handleVote={inspirationData.vote}
          handleUnVote={inspirationData.unVote}
          onLoadMore={handleInspirationLoadMore}
        />
      </div>
    </div>
  );
};
